import { get, noop } from "lodash";
import uwtService from "@tvg/api/uwt";

import RG_OPTIONS from "@tvg/types/ResponsibleGaming";
import mediator from "@tvg/mediator";

export const MODULE_NAME = RG_OPTIONS.FUNDING_CONTROLS;

export const MODAL_STATUS = {
  CLOSED: "",
  CONFIRM: "CONFIRM",
  SUCCESS: "SUCCESS",
  ERROR: "ERROR"
};

export const blockGtmModalOpen = (eventLabel) => {
  mediator.base.dispatch({
    type: "RG_BLOCK_MODAL_OPEN",
    payload: {
      gaEventLabel: eventLabel
    }
  });
};

export const blockGtmHandle = (eventLabel) => {
  mediator.base.dispatch({
    type: "RG_MODAL_CLICK_HANDLE",
    payload: {
      gaEventLabel: eventLabel,
      module: MODULE_NAME
    }
  });
};

/*
  VIWD - Visa Withdrawals should not be considered for Funding Controls
  ITWD - Interchecks Withdrawals should not be considered for Funding Controls
*/
const parsePaymentMethodData = (method) => ({
  isBlocked:
    get(method, "responsibleIsBlocked", false) ||
    !get(method, "isDepositAvailable", true),
  name: get(method, "paymentType", ""),
  unblockDate: get(method, "responsibleUnblockDate")
});

export const invalidFundingMethods = [
  "IBC",
  "DEBS",
  "CCS",
  "CASH",
  "APPL",
  "VIWD",
  "ITWD",
  "OTR",
  "WTRN",
  "CHK"
];

export const validFundingMethod = ({ name }) =>
  !invalidFundingMethods.includes(name);

// Codes used by the legacy CCP credit/debit deposit flow.
const LEGACY_CC_CODES = ["CC", "DEB"];
// Codes used by the Braintree credit/debit deposit flow.
const BRAINTREE_CC_CODES = ["BTCC", "BTDB"];

/**
 * The UWT `payment-options` endpoint may return both the legacy CCP card codes
 * (`CC`/`DEB`) and the Braintree card codes (`BTCC`/`BTDB`) for a user during
 * the Braintree rollout. Funding Controls should only show one Credit Card and
 * one Debit Card row per user, picked based on which deposit flow is currently
 * active for them. Backend blocks both legacy and Braintree variants atomically
 * when either is blocked, so hiding one side here does not affect the
 * responsible-gaming guarantee.
 */
const isHiddenByBraintreeToggle = (paymentType, isBraintreeCCEnabled) => {
  if (isBraintreeCCEnabled) {
    return LEGACY_CC_CODES.includes(paymentType);
  }
  return BRAINTREE_CC_CODES.includes(paymentType);
};

const fetchPaymentMethods = (
  accountNumber,
  isLogged,
  setActiveMethods,
  setBlockedMethods,
  setError = noop,
  isMZMToggleOn = false,
  isBraintreeCCEnabled = false
) => {
  if (accountNumber && isLogged) {
    return uwtService
      .getPaymentOptions(accountNumber)
      .then((res) => {
        let parsedMethod = null;
        const parsedActiveMethods = [];
        const parsedBlockedMethods = [];
        get(res, "data", []).forEach((method) => {
          if (!isMZMToggleOn && method.paymentType === "MZM") {
            return;
          }
          if (
            isHiddenByBraintreeToggle(method.paymentType, isBraintreeCCEnabled)
          ) {
            return;
          }
          parsedMethod = parsePaymentMethodData(method);
          if (parsedMethod && parsedMethod.isBlocked)
            parsedBlockedMethods.push(parsedMethod);
          else if (parsedMethod) parsedActiveMethods.push(parsedMethod);
        });
        setActiveMethods(parsedActiveMethods.filter(validFundingMethod));
        setBlockedMethods(parsedBlockedMethods.filter(validFundingMethod));
      })
      .catch(() => {
        setError(true);
      });
  }

  setActiveMethods([]);
  setBlockedMethods([]);

  return Promise.resolve({});
};

const blockPaymentMethod = (accountNumber, paymentType) =>
  uwtService.blockPaymentMethod(accountNumber, paymentType);

export default {
  fetchPaymentMethods,
  blockPaymentMethod
};
